import { TReportGrouped } from "@reports/data-access/entities";

/**
 * Interface for elastic search UI client settings
 * Mirrors the localStorage structure currently used in elastic-search.component.ts
 */
export interface IUiClientSettingsElasticSearch {
  /** Search options configuration (tabs enabled/active) */
  SearchOptions: Array<{
    Type: string;
    Enabled: boolean;
    TabActive: boolean;
  }>;
  /** Person action toggles configuration */
  PersonActions: Array<{
    Id: string;
    Enabled: boolean;
  }>;
}

/**
 * Interface for sidebar UI client settings
 * Mirrors the localStorage structure currently used in navbar.store.ts
 */
export interface IUiClientSettingsSidebar {
  /** Whether the sidebar is pinned */
  Pinned: boolean;
  /** Sidebar width in pixels (clamped between 200-400) */
  Width: number;
}

/**
 * Interface for the footer keyboard-shortcut settings
 * `Overrides` is keyed by the footer action id; `null` means the user removed
 * that action's key, so only the click target remains.
 */
export interface IUiClientSettingsFooterShortcuts {
  /** Master off-switch — every footer shortcut stops responding while true. */
  Disabled: boolean;
  /** Per-action key override; absent = the shipped default key. */
  Overrides: Record<string, string | null>;
}

/**
 * Interface for the mini-monitor (Access Panel) preferences.
 * F2 releases the entry and F3 the exit of the focused access point in the
 * edge-control widget; either can be switched off from the panel's gear menu.
 * The same group holds the refusal sound of the detached mini-monitor and the
 * operating-system notification for the same refusals when it is not detached.
 */
export interface IUiClientSettingsMiniMonitorShortcuts {
  /** F2 (release entry) answers while true. */
  F2Enabled: boolean;
  /** F3 (release exit) answers while true. */
  F3Enabled: boolean;
  /**
   * A beep when a refusal that can still be released arrives, with the
   * mini-monitor detached. Off unless explicitly true.
   */
  RefusalSoundEnabled: boolean;
  /**
   * An operating-system notification when a refusal that can still be
   * released arrives, with the mini-monitor not detached. Off unless
   * explicitly true.
   */
  RefusalNotificationEnabled: boolean;
}

/** Profile header pinned action IDs (max 4). Edit Profile ('1') is always first and non-removable. */
const DEFAULT_PROFILE_HEADER_PINNED_ACTIONS = ['1'];

/**
 * Size of the shortcut icon buttons. A subset of the `size` input of `lib-icon-button`
 * ('small' is not offered — the shortcut icons get unreadable at 34px).
 */
export type TShortcutsIconSize = 'default' | 'large';

/** Valid shortcut icon sizes, used to reject garbage coming from the stored JSON */
const SHORTCUTS_ICON_SIZES = new Set<string>(['default', 'large'] satisfies TShortcutsIconSize[]);

/** Icon size used when the user never picked one */
export const DefaultShortcutsIconSize: TShortcutsIconSize = 'default';

/** As escolhas de tempo de espera do não identificado, em minutos (Q21). */
export const UNIDENTIFIED_WAIT_MINUTES_OPTIONS = [2, 5, 10] as const;
export type TUnidentifiedWaitMinutes = (typeof UNIDENTIFIED_WAIT_MINUTES_OPTIONS)[number];

/**
 * Preferências do Controle de acesso no celular. Ficam aqui, e não no aparelho,
 * para acompanhar a pessoa em qualquer celular (P18).
 */
export interface IUiClientSettingsAccessRemoteControl {
  /** Quanto o "Não identificado" fica na fila antes de sair sozinho. */
  UnidentifiedWaitMinutes: TUnidentifiedWaitMinutes;
}

/**
 * Main interface for all UI client settings
 * This is stored as a JSON blob in the UserSettings.UiClientSettings field
 */
export interface IUiClientSettings {
  /** Elastic search preferences */
  ElasticSearch?: IUiClientSettingsElasticSearch;
  /** Sidebar preferences */
  Sidebar?: IUiClientSettingsSidebar;
  /** Optional: core shortcuts preferences for future use */
  Shortcuts?: {
    /** Ordering or visibility overrides for shortcuts */
    Ordering?: string[];
    /** Hidden shortcuts */
    Hidden?: string[];
    /** Size of the shortcut icon buttons */
    IconSize?: TShortcutsIconSize;
  };
  GroupedReports: TReportGrouped[];
  /**
   * Per-grid "remember last filter" toggle, keyed by the grid's
   * `persistFilterKey` (e.g. `people-contracts`). `true` = the grid restores its
   * last header-filter selection on open; absence defaults to enabled.
   */
  GridFilterPrefs?: Record<string, boolean>;
  /** Pinned profile header action IDs (max 4). Edit Profile is always included. */
  ProfileHeaderPinnedActions?: string[];
  /** User-defined order of the profile navigation tabs, by tab id. Empty = default order. */
  ProfileTabsOrder?: string[];
  /** Frequency dashboard preferences */
  FrequencyDashboard?: {
    EnabledCategories: string[];
  };
  /** Footer keyboard-shortcut preferences */
  FooterShortcuts?: IUiClientSettingsFooterShortcuts;
  /** Mini-monitor (Access Panel) keyboard-shortcut preferences */
  MiniMonitorShortcuts?: IUiClientSettingsMiniMonitorShortcuts;
  /** Controle de acesso no celular: o tempo de espera do não identificado. */
  AccessRemoteControl?: IUiClientSettingsAccessRemoteControl;
}

/** Default values for sidebar settings */
export const SidebarMinWidth = 200;
export const SidebarMaxWidth = 400;
export const SidebarDefaultWidth = 253;

/** Search option types matching elastic-search.model.ts (first is active by default) */
const DEFAULT_SEARCH_TYPES = ['All', 'Customer', 'Visitor', 'User', 'Action'] as const;

/** Default elastic search search options (PascalCase storage format) */
const DefaultSearchOptions: IUiClientSettingsElasticSearch['SearchOptions'] =
  DEFAULT_SEARCH_TYPES.map((type, index) => ({
    Type: type,
    Enabled: true,
    TabActive: index === 0,
  }));

/** Person action ids matching elastic-search.model.ts */
const DEFAULT_PERSON_ACTION_IDS = [
  'EDIT_INFO',
  'OPEN_WHATSAPP',
  'OPEN_CHAT',
  'OPEN_ACCESS_EDGES',
  'NEW_SUBSCRIPTION',
  'NEW_SALE',
  'NEW_SERVICE',
  'NEW_RECEIPT',
] as const;

/** Default person actions (PascalCase storage format) */
const DefaultPersonActions: IUiClientSettingsElasticSearch['PersonActions'] =
  DEFAULT_PERSON_ACTION_IDS.map((id) => ({
    Id: id,
    Enabled: true,
  }));

/** Default UI client settings used when API returns null/undefined or on GET failure */
export const DefaultUiClientSettings: IUiClientSettings = {
  ElasticSearch: {
    SearchOptions: DefaultSearchOptions,
    PersonActions: DefaultPersonActions,
  },
  Sidebar: {
    Pinned: false,
    Width: SidebarDefaultWidth,
  },
  Shortcuts: {
    IconSize: DefaultShortcutsIconSize,
  },
  GroupedReports: [],
  GridFilterPrefs: {},
  ProfileHeaderPinnedActions: DEFAULT_PROFILE_HEADER_PINNED_ACTIONS,
  ProfileTabsOrder: [],
  FrequencyDashboard: {
    EnabledCategories: ['yesterday', '7days', '15days', '30days'],
  },
  FooterShortcuts: {
    Disabled: false,
    Overrides: {},
  },
  MiniMonitorShortcuts: {
    F2Enabled: true,
    F3Enabled: true,
    RefusalSoundEnabled: false,
    RefusalNotificationEnabled: false,
  },
  AccessRemoteControl: {
    UnidentifiedWaitMinutes: 2,
  },
};

/**
 * Every key `parseUiClientSettings` reads: the PascalCase names and the older
 * camelCase spellings. Anything else was written by a newer client. This
 * version does not understand it but carries it through — the whole blob is
 * written back on every save, and dropping it here would erase it for every
 * other browser and every newer client.
 */
const KNOWN_UI_CLIENT_SETTINGS_KEYS = new Set([
  'ElasticSearch',
  'elasticSearch',
  'Sidebar',
  'sidebar',
  'Shortcuts',
  'coreShortcuts',
  'GroupedReports',
  'groupedReports',
  'GridFilterPrefs',
  'gridFilterPrefs',
  'ProfileHeaderPinnedActions',
  'profileHeaderPinnedActions',
  'ProfileTabsOrder',
  'profileTabsOrder',
  'FrequencyDashboard',
  'frequencyDashboard',
  'FooterShortcuts',
  'footerShortcuts',
  'MiniMonitorShortcuts',
  'miniMonitorShortcuts',
  'AccessRemoteControl',
]);

/** The keys of a stored blob that this version does not read, untouched. */
function unknownUiClientSettings(
  parsed: Record<string, unknown>
): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(parsed).filter(
      ([key]) => !KNOWN_UI_CLIENT_SETTINGS_KEYS.has(key)
    )
  );
}

/**
 * Parses UiClientSettings from API response
 * Handles both string (JSON) and object formats
 * Falls back to defaults on parsing error
 */
export function parseUiClientSettings(
  value: string | IUiClientSettings | null | undefined
): IUiClientSettings {
  if (!value) {
    return DefaultUiClientSettings;
  }

  try {
    const parsed =
      typeof value === 'string' ? JSON.parse(value) : value;

    const es = parsed.ElasticSearch ?? parsed.elasticSearch;
    const sidebar = parsed.Sidebar ?? parsed.sidebar;
    const shortcuts = parsed.Shortcuts ?? parsed.coreShortcuts;
    const groupedReports = parsed.GroupedReports ?? parsed.groupedReports;
    const profileHeaderPinned = parsed.ProfileHeaderPinnedActions ?? parsed.profileHeaderPinnedActions;

    return {
      ...unknownUiClientSettings(parsed),
      ElasticSearch: {
        SearchOptions:
          es?.SearchOptions ?? es?.searchOptions ?? DefaultUiClientSettings.ElasticSearch!.SearchOptions,
        PersonActions:
          es?.PersonActions ?? es?.personActions ?? DefaultUiClientSettings.ElasticSearch!.PersonActions,
      },
      Sidebar: {
        Pinned:
          sidebar?.Pinned ?? sidebar?.pinned ?? DefaultUiClientSettings.Sidebar!.Pinned,
        Width: clampSidebarWidth(
          sidebar?.Width ?? sidebar?.width ?? DefaultUiClientSettings.Sidebar!.Width
        ),
      },
      Shortcuts: {
        ...(shortcuts ?? {}),
        IconSize: normalizeShortcutsIconSize(shortcuts?.IconSize ?? shortcuts?.iconSize),
      },
      GroupedReports: groupedReports ?? DefaultUiClientSettings.GroupedReports,
      GridFilterPrefs: parsed.GridFilterPrefs ?? parsed.gridFilterPrefs ?? DefaultUiClientSettings.GridFilterPrefs,
      ProfileHeaderPinnedActions: normalizeProfileHeaderPinnedActions(profileHeaderPinned),
      ProfileTabsOrder:
        parsed.ProfileTabsOrder ?? parsed.profileTabsOrder ?? DefaultUiClientSettings.ProfileTabsOrder,
      FrequencyDashboard: parsed.FrequencyDashboard ?? parsed.frequencyDashboard ?? DefaultUiClientSettings.FrequencyDashboard,
      FooterShortcuts: parsed.FooterShortcuts ?? parsed.footerShortcuts ?? DefaultUiClientSettings.FooterShortcuts,
      MiniMonitorShortcuts: normalizeMiniMonitorShortcuts(
        parsed.MiniMonitorShortcuts ?? parsed.miniMonitorShortcuts
      ),
      AccessRemoteControl: normalizeAccessRemoteControl(parsed.AccessRemoteControl),
    };
  } catch (error) {
    console.warn('Failed to parse UiClientSettings, using defaults:', error);
    return DefaultUiClientSettings;
  }
}

/**
 * Clamps sidebar width to valid range
 */
export function clampSidebarWidth(width: number): number {
  return Math.max(SidebarMinWidth, Math.min(SidebarMaxWidth, width));
}

/** Falls back to the default when the stored value is missing or not a known size */
export function normalizeShortcutsIconSize(
  value: string | null | undefined
): TShortcutsIconSize {
  return value && SHORTCUTS_ICON_SIZES.has(value)
    ? (value as TShortcutsIconSize)
    : DefaultShortcutsIconSize;
}

/**
 * Only an explicit `false` turns a mini-monitor shortcut off — anything else
 * (absent key, stale value from an older client, garbage in the stored JSON)
 * reads as enabled, which is how the shortcuts shipped. The refusal sound and
 * the refusal notification are the opposite: only an explicit `true` turns them
 * on, so neither arrives unasked at a front desk. Keys this version does not
 * know (a newer client's preferences in the same group) pass through.
 */
export function normalizeMiniMonitorShortcuts(
  value: Partial<IUiClientSettingsMiniMonitorShortcuts> | null | undefined
): IUiClientSettingsMiniMonitorShortcuts {
  return {
    ...value,
    F2Enabled: value?.F2Enabled !== false,
    F3Enabled: value?.F3Enabled !== false,
    RefusalSoundEnabled: value?.RefusalSoundEnabled === true,
    RefusalNotificationEnabled: value?.RefusalNotificationEnabled === true,
  };
}

const MAX_PROFILE_HEADER_PINNED = 4;

/** Ensures Edit Profile ('1') is first and list is valid (max 4 items) */
function normalizeProfileHeaderPinnedActions(
  value: string[] | null | undefined
): string[] {
  const arr = Array.isArray(value) ? value.slice(0, MAX_PROFILE_HEADER_PINNED) : [];
  if (!arr.includes('1')) {
    return ['1', ...arr.filter((x) => x !== '1')].slice(0, MAX_PROFILE_HEADER_PINNED);
  }
  return arr;
}

/**
 * Merges partial UiClientSettings update with existing settings
 */
/**
 * Um tempo de espera que não seja 2, 5 ou 10 — ausente, escrito por um cliente
 * futuro, digitado à mão — vale 2 minutos, o padrão (Q21).
 */
export function normalizeAccessRemoteControl(
  value: Partial<IUiClientSettingsAccessRemoteControl> | null | undefined
): IUiClientSettingsAccessRemoteControl {
  const minutes = value?.UnidentifiedWaitMinutes;
  return {
    UnidentifiedWaitMinutes: UNIDENTIFIED_WAIT_MINUTES_OPTIONS.includes(
      minutes as TUnidentifiedWaitMinutes
    )
      ? (minutes as TUnidentifiedWaitMinutes)
      : 2,
  };
}

export function mergeUiClientSettings(
  existing: IUiClientSettings,
  partial: Partial<IUiClientSettings>
): IUiClientSettings {
  const partialEs = partial.ElasticSearch;
  const partialSidebar = partial.Sidebar;

  const partialProfilePinned = partial.ProfileHeaderPinnedActions;
  const mergedProfilePinned =
    partialProfilePinned !== undefined
      ? normalizeProfileHeaderPinnedActions(partialProfilePinned)
      : existing.ProfileHeaderPinnedActions ?? DefaultUiClientSettings.ProfileHeaderPinnedActions;

  return {
    // Unknown groups of the existing settings come along untouched.
    ...existing,
    ElasticSearch: partialEs
      ? {
          SearchOptions:
            partialEs.SearchOptions ??
            existing.ElasticSearch?.SearchOptions ??
            DefaultUiClientSettings.ElasticSearch!.SearchOptions,
          PersonActions:
            partialEs.PersonActions ??
            existing.ElasticSearch?.PersonActions ??
            DefaultUiClientSettings.ElasticSearch!.PersonActions,
        }
      : existing.ElasticSearch,
    Sidebar: partialSidebar
      ? {
          Pinned:
            partialSidebar.Pinned ??
            existing.Sidebar?.Pinned ??
            DefaultUiClientSettings.Sidebar!.Pinned,
          Width: clampSidebarWidth(
            partialSidebar.Width ??
              existing.Sidebar?.Width ??
              DefaultUiClientSettings.Sidebar!.Width
          ),
        }
      : existing.Sidebar,
    Shortcuts: partial.Shortcuts
      ? { ...existing.Shortcuts, ...partial.Shortcuts }
      : existing.Shortcuts,
    GroupedReports: partial.GroupedReports ?? existing.GroupedReports,
    GridFilterPrefs: partial.GridFilterPrefs ?? existing.GridFilterPrefs,
    ProfileHeaderPinnedActions: mergedProfilePinned,
    ProfileTabsOrder:
      partial.ProfileTabsOrder ?? existing.ProfileTabsOrder ?? DefaultUiClientSettings.ProfileTabsOrder,
    FrequencyDashboard: partial.FrequencyDashboard ?? existing.FrequencyDashboard,
    FooterShortcuts: partial.FooterShortcuts ?? existing.FooterShortcuts,
    // Key by key: changing F2 must not reset the group's other keys.
    MiniMonitorShortcuts:
      partial.MiniMonitorShortcuts !== undefined
        ? normalizeMiniMonitorShortcuts({
            ...existing.MiniMonitorShortcuts,
            ...partial.MiniMonitorShortcuts,
          })
        : existing.MiniMonitorShortcuts ?? DefaultUiClientSettings.MiniMonitorShortcuts,
    AccessRemoteControl:
      partial.AccessRemoteControl !== undefined
        ? normalizeAccessRemoteControl({
            ...existing.AccessRemoteControl,
            ...partial.AccessRemoteControl,
          })
        : existing.AccessRemoteControl ?? DefaultUiClientSettings.AccessRemoteControl,
  };
}

export function applyUiClientSettingsPatches(
  base: IUiClientSettings,
  patches: Partial<IUiClientSettings>[]
): IUiClientSettings {
  return patches.reduce<IUiClientSettings>(
    (settings, patch) => mergeUiClientSettings(settings, patch),
    base
  );
}

/**
 * Local interface matching ISearchOption structure from elastic-search.model.ts
 */
interface ISearchOptionLocal {
  type: string;
  enabled: boolean;
  tabActive: boolean;
  [key: string]: any;
}

/**
 * Local interface matching IPersonActionOption structure from elastic-search.model.ts
 */
interface IPersonActionOptionLocal {
  id: string;
  enabled: boolean;
  [key: string]: any;
}

/**
 * Converts elastic search options to storage format (PascalCase)
 */
export function searchOptionsToStorageFormat(
  options: ISearchOptionLocal[]
): IUiClientSettingsElasticSearch['SearchOptions'] {
  return options.map((option) => ({
    Type: option.type,
    Enabled: option.enabled,
    TabActive: option.tabActive,
  }));
}

/**
 * Converts person actions to storage format (PascalCase)
 */
export function personActionsToStorageFormat(
  actions: IPersonActionOptionLocal[]
): IUiClientSettingsElasticSearch['PersonActions'] {
  return actions.map((action) => ({
    Id: action.id,
    Enabled: action.enabled,
  }));
}

/**
 * Converts storage format search options (PascalCase) to full search options (camelCase for UI)
 */
export function storageFormatToSearchOptions(
  storedOptions: IUiClientSettingsElasticSearch['SearchOptions'] | undefined,
  defaultOptions: ISearchOptionLocal[]
): ISearchOptionLocal[] {
  if (!storedOptions || storedOptions.length === 0) {
    return defaultOptions;
  }

  return defaultOptions.map((defaultOption) => {
    const stored = storedOptions.find((opt) => opt.Type === defaultOption.type);
    return stored
      ? {
          ...defaultOption,
          enabled: stored.Enabled,
          tabActive: stored.TabActive,
        }
      : defaultOption;
  });
}

/**
 * Converts storage format person actions (PascalCase) to full person actions (camelCase for UI)
 */
export function storageFormatToPersonActions(
  storedActions: IUiClientSettingsElasticSearch['PersonActions'] | undefined,
  defaultActions: IPersonActionOptionLocal[]
): IPersonActionOptionLocal[] {
  if (!storedActions || storedActions.length === 0) {
    return defaultActions;
  }

  return defaultActions.map((defaultAction) => {
    const stored = storedActions.find((action) => action.Id === defaultAction.id);
    return stored
      ? { ...defaultAction, enabled: stored.Enabled }
      : defaultAction;
  });
}
